import {type EnrichedAstNode} from 'rendition-core';
import {uniq} from 'lodash';
import {useCallback, useMemo} from 'react';
import {SetViewOptions, useViewContext} from 'Contexts/ViewContext';

/**
 * A React hook to handle multiple node values with a single state.
 *
 * @template T
 * @param {EnrichedAstNode[]} selectedNodes - An array of selected nodes.
 * @param {(node: EnrichedAstNode) => T | undefined} accessFn - A function to access the value from a node.
 * @param {(node: EnrichedAstNode, value: T) => void} assignFn - A function to assign a value to a node.
 * @param {(value: T | undefined) => boolean} [filterFn] - An optional filter function to include/exclude values.
 * @param {Partial<SetViewOptions>} [setViewOptions] - An optional object to set the view state.
 *
 * @returns {{
 *  allValuesEqual: boolean,
 *  updateNodes: (value: T) => void,
 *  value: T | undefined
 * }}
 */
export function useMultiNodeValue<T>(
    selectedNodes: EnrichedAstNode[],
    accessFn: (node: EnrichedAstNode) => T | undefined,
    assignFn: (node: EnrichedAstNode, value: T) => void,
    filterFn?: (value: T | undefined) => boolean,
    setViewOptions?: Partial<SetViewOptions>
): {
    allValuesEqual: boolean;
    updateNodes: (value: T) => void;
    value: T | undefined;
} {
    const {updateView} = useViewContext();

    // Get unique values from the selected nodes using accessFn and filterFn.
    const uniqueValues = useMemo(() => {
        const values = selectedNodes.map(accessFn).filter(!!filterFn ? filterFn : () => true);
        return uniq(values);
    }, [accessFn, filterFn, selectedNodes]);

    // Check if all values are equal.
    const allValuesEqual = uniqueValues.length === 1;

    // Update nodes with the given value using assignFn and update the view state.
    const updateNodes = useCallback(
        (value: T) => {
            updateView((currentView) => {
                selectedNodes.map((node) => assignFn(node, value));
                return currentView;
            }, setViewOptions);
        },
        [assignFn, selectedNodes, updateView, setViewOptions]
    );

    return {allValuesEqual, updateNodes, value: allValuesEqual ? uniqueValues[0] : undefined};
}
